import React, {useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import Calendar from 'react-calendar';
import {useDatabase} from '../context/DatabaseContext';
import {useToast} from '../context/ToastContext';
import {useUserNickname} from '../hooks/useUserNickname';
import {colors} from '../theme/colors';
import 'react-calendar/dist/Calendar.css';

const FoodDiaryScreen = () => {
  const {getEntriesByDate, deleteEntry, getAllFoods, getAllDates} = useDatabase();
  const {success, error} = useToast();
  const userNickname = useUserNickname();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [foods, setFoods] = useState([]);
  const [markedDates, setMarkedDates] = useState(new Set());
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (location.pathname === '/food-diary') {
      loadFoods();
      loadEntriesForDate(selectedDate);
      loadDates();
    }
  }, [location.pathname, selectedDate]);

  const loadFoods = async () => {
    const foodsList = await getAllFoods();
    setFoods(foodsList || []);
  };

  const loadDates = async () => {
    const dates = await getAllDates();
    const markedSet = new Set(dates);
    setMarkedDates(markedSet);
  };

  const loadEntriesForDate = async (date) => {
    const dateString = date.toISOString().split('T')[0];
    const dateEntries = await getEntriesByDate(dateString);
    setEntries(dateEntries);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
    loadEntriesForDate(newDate);
  };

  const onDayClick = (date) => {
    setSelectedDate(date);
    loadEntriesForDate(date);
    setShowCalendar(false);
  };

  const tileClassName = ({date, view}) => {
    if (view === 'month') {
      const dateString = date.toISOString().split('T')[0];
      const isMarked = markedDates.has(dateString);
      const isSelected = date.toDateString() === selectedDate.toDateString();
      
      if (isSelected) {
        return 'react-calendar__tile--selected';
      }
      if (isMarked) {
        return 'react-calendar__tile--marked';
      }
    }
    return null;
  };

  const tileContent = ({date, view}) => {
    if (view === 'month') {
      const dateString = date.toISOString().split('T')[0];
      if (markedDates.has(dateString)) {
        return <div style={styles.calendarDot} />;
      }
    }
    return null;
  };

  const handleDelete = async (entryId) => {
    const confirmed = window.confirm('האם אתה בטוח שברצונך למחוק רשומה זו?');
    if (confirmed) {
      const deleted = await deleteEntry(entryId);
      if (deleted) {
        success('הרשומה נמחקה בהצלחה');
        loadEntriesForDate(selectedDate);
        loadDates(); // Refresh marked dates
      } else {
        error('שגיאה במחיקת הרשומה');
      }
    }
  };

  const handleClearAll = async () => {
    if (entries.length === 0) {
      return;
    }
    
    const confirmed = window.confirm(`האם אתה בטוח שברצונך למחוק את כל ${entries.length} הרשומות של היום?`);
    if (confirmed) {
      try {
        // Delete all entries for this date
        let successCount = 0;
        let failCount = 0;
        
        for (const entry of entries) {
          const deleted = await deleteEntry(entry.id);
          if (deleted) {
            successCount++;
          } else {
            failCount++;
          }
        }
        
        if (successCount > 0) {
          success(`נמחקו ${successCount} רשומות בהצלחה`);
          loadEntriesForDate(selectedDate);
          loadDates(); // Refresh marked dates
        }
        
        if (failCount > 0) {
          error(`נכשל במחיקת ${failCount} רשומות`);
        }
      } catch (err) {
        console.error('Error clearing all entries:', err);
        error('שגיאה במחיקת הרשומות');
      }
    }
  };

  // Get Hebrew unit label helper
  const getHebrewUnitLabel = (unit, amount = 1) => {
    if (unit === 'unit') {
      return amount === 1 ? 'יחידה' : 'יחידות';
    } else if (unit === 'gram') {
      return 'גרם';
    } else if (unit === 'tablespoon') {
      return amount === 1 ? 'כף' : 'כפות';
    } else if (unit === 'teaspoon') {
      return amount === 1 ? 'כפית' : 'כפיות';
    }
    return unit;
  };

  // Format quantity display for entry
  const formatQuantity = (entry) => {
    const food = foods.find(f => f.id === entry.food_id);
    if (!food) {
      return `${entry.grams.toFixed(0)} ${getHebrewUnitLabel('gram')}`;
    }

    // Check if food uses new schema (base portion) or old schema (per 100g)
    const isBasePortionFood = food.base_amount !== undefined && 
                              food.base_amount_unit !== undefined &&
                              food.calories !== undefined;

    if (isBasePortionFood) {
      if (food.base_amount_unit === 'unit') {
        // For unit-based foods, we need to estimate units from grams
        // Since entries store grams, and unit foods don't have a direct gram conversion,
        // we'll estimate based on calories ratio
        if (food.calories > 0) {
          const portions = entry.calories / food.calories;
          const units = portions * food.base_amount;
          if (units >= 0.1) {
            if (units < 1) {
              return `${units.toFixed(1)} ${getHebrewUnitLabel('unit', units)}`;
            } else {
              return `${units.toFixed(1)} ${getHebrewUnitLabel('unit', units)}`;
            }
          }
        }
        // Fallback to grams if calculation fails
        return `${entry.grams.toFixed(0)} ${getHebrewUnitLabel('gram')}`;
      } else if (food.base_amount_unit === 'gram') {
        // For gram-based foods, calculate portions and show in grams
        const portions = entry.grams / food.base_amount;
        const totalGrams = portions * food.base_amount;
        return `${totalGrams.toFixed(0)} ${getHebrewUnitLabel('gram')}`;
      } else if (food.base_amount_unit === 'tablespoon') {
        // For tablespoon-based foods, calculate tablespoons
        const portions = entry.grams / (food.base_amount * 15); // 1 tbsp = 15g
        const tbsp = portions * food.base_amount;
        if (tbsp >= 0.1) {
          return `${tbsp.toFixed(1)} ${getHebrewUnitLabel('tablespoon', tbsp)}`;
        }
        // Fallback to grams
        return `${entry.grams.toFixed(0)} ${getHebrewUnitLabel('gram')}`;
      }
    }
    
    // Old schema: per 100g - just show grams
    return `${entry.grams.toFixed(0)} ${getHebrewUnitLabel('gram')}`;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.userName}>{userNickname} ❤️</div>
          <button
            style={styles.backButton}
            onClick={() => navigate('/')}
            aria-label="Go back to home"
            type="button">
            <span aria-hidden="true">←</span>
          </button>
        </div>
        <div style={styles.headerCenter}>
          <div style={styles.profileIcon} aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="18" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <path d="M12 16 Q20 8 28 16" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <circle cx="20" cy="20" r="3" fill={colors.primary}/>
            </svg>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.bellIcon} aria-hidden="true">🔔</div>
        </div>
      </div>

      {/* Date Navigation */}
      <div style={styles.dateNavigation}>
        <button
          style={styles.dateNavButton}
          onClick={() => navigateDate('prev')}
          aria-label="Previous day"
          type="button">
          <span aria-hidden="true">←</span>
        </button>
        <button
          style={styles.dateText}
          onClick={() => setShowCalendar(!showCalendar)}
          aria-label={`Selected date: ${formatDate(selectedDate)}. Click to open calendar`}
          aria-expanded={showCalendar}
          type="button">
          יומן ארוחות - {formatDate(selectedDate)}
        </button>
        <button
          style={styles.dateNavButton}
          onClick={() => navigateDate('next')}
          aria-label="Next day"
          type="button">
          <span aria-hidden="true">→</span>
        </button>
      </div>

      {/* Calendar View */}
      {showCalendar && (
        <div style={styles.calendarContainer}>
          <style>{`
            .react-calendar {
              background-color: ${colors.surface};
              border: 1px solid ${colors.border};
              border-radius: 15px;
              color: ${colors.textPrimary};
              font-family: inherit;
              width: 100%;
            }
            .react-calendar__navigation {
              background-color: ${colors.surface};
              border-bottom: 1px solid ${colors.border};
            }
            .react-calendar__navigation button {
              color: ${colors.textPrimary};
              font-weight: bold;
            }
            .react-calendar__navigation button:enabled:hover,
            .react-calendar__navigation button:enabled:focus {
              background-color: ${colors.surfaceAlt};
            }
            .react-calendar__month-view__weekdays {
              background-color: ${colors.surface};
              color: ${colors.textPrimary};
              font-weight: 600;
            }
            .react-calendar__month-view__weekdays__weekday {
              padding: 0.5em;
            }
            .react-calendar__tile {
              color: ${colors.textPrimary};
              background-color: ${colors.surface};
            }
            .react-calendar__tile:enabled:hover,
            .react-calendar__tile:enabled:focus {
              background-color: ${colors.surfaceAlt};
            }
            .react-calendar__tile--active {
              background-color: ${colors.primary};
              color: ${colors.textOnPrimary};
            }
            .react-calendar__tile--now {
              background-color: ${colors.bg};
            }
            .react-calendar__tile--marked {
              position: relative;
            }
          `}</style>
          <Calendar
            onChange={onDayClick}
            value={selectedDate}
            tileClassName={tileClassName}
            tileContent={tileContent}
            className="custom-calendar"
          />
        </div>
      )}

      {/* Table Headers */}
      <div style={styles.tableHeaders} role="row">
        <div style={styles.tableHeader} role="columnheader">תיאור</div>
        <div style={styles.tableHeader} role="columnheader">כמות</div>
        <div style={styles.tableHeader} role="columnheader">קלוריות</div>
      </div>

      {/* Entries List */}
      <div style={styles.entriesList}>
        {entries.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateText}>אין רשומות לתאריך זה</div>
          </div>
        ) : (
          <>
            {entries.map(entry => (
              <div key={entry.id} style={styles.entryRow}>
                <button
                  style={styles.deleteButton}
                  onClick={() => handleDelete(entry.id)}
                  aria-label={`Delete entry: ${entry.food_name}`}
                  type="button">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
                      stroke="#4A9EFF"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <div style={styles.entryDescription}>{entry.food_name}</div>
                <div style={styles.entryQuantity}>{formatQuantity(entry)}</div>
                <div style={styles.entryCalories}>{entry.calories.toFixed(1)}</div>
              </div>
            ))}
            
            {/* Clear All Button */}
            {entries.length > 0 && (
              <div style={styles.clearAllContainer}>
                <button
                  style={styles.clearAllButton}
                  onClick={handleClearAll}
                  className="btn-secondary"
                  aria-label={`Delete all ${entries.length} entries for today`}
                  type="button">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{marginLeft: 8}} aria-hidden="true">
                    <path
                      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
                      stroke={colors.error}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span style={styles.clearAllButtonText}>מחק הכל ({entries.length})</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
    paddingBottom: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    paddingTop: '20px',
  },
  headerLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  userName: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: 'bold',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.textPrimary,
    fontSize: 24,
    cursor: 'pointer',
    padding: 8,
  },
  headerCenter: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  profileIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.surface,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `2px solid ${colors.primary}`,
  },
  headerRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  bellIcon: {
    fontSize: 24,
    color: colors.textPrimary,
  },
  dateNavigation: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 20px',
    gap: 12,
  },
  dateNavButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: 20,
    cursor: 'pointer',
    padding: 8,
  },
  dateText: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: 500,
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: 8,
    transition: 'background-color 200ms ease',
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
  },
  calendarContainer: {
    padding: '0 20px',
    marginBottom: 20,
  },
  calendarDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.primary,
    position: 'absolute',
    bottom: 4,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  tableHeaders: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: `1px solid ${colors.border}`,
    marginBottom: 8,
  },
  tableHeader: {
    fontSize: 14,
    color: colors.textTertiary,
    fontWeight: 600,
    flex: 1,
    textAlign: 'center',
  },
  entriesList: {
    padding: '0 20px',
  },
  entryRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: `1px solid ${colors.border}`,
    gap: 12,
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
  },
  entryDescription: {
    fontSize: 14,
    color: colors.textPrimary,
    flex: 2,
    textAlign: 'right',
  },
  entryQuantity: {
    fontSize: 14,
    color: colors.textTertiary,
    flex: 1,
    textAlign: 'center',
  },
  entryCalories: {
    fontSize: 14,
    color: colors.primary,
    flex: 1,
    textAlign: 'left',
    fontWeight: 600,
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    color: colors.textSecondary,
  },
  clearAllContainer: {
    padding: '20px 0',
    marginTop: 20,
    borderTop: `1px solid ${colors.border}`,
  },
  clearAllButton: {
    width: '100%',
    height: 48,
    backgroundColor: colors.surface,
    color: colors.error,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
  },
  clearAllButtonText: {
    color: colors.error,
  },
};

export default FoodDiaryScreen;

